<template>
  <div class="permissions-matrix">
    <div class="d-flex justify-content-between align-items-center mb-1 mt-3">
      <p class="mb-0" style="font-weight: 1000; color: black">
        Role Template Permissions
      </p>
    </div>

    <div class="table-responsive">
      <table class="table table-bordered">
        <thead class="table-light">
          <tr>
            <th class="role-column">ROLES</th>
            <th
              v-for="col in permissionColumns"
              :key="col.key"
              class="permission-header"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="role in roleList" :key="role.id">
            <td class="role-info">{{ role.name }}</td>
            <td
              v-for="col in permissionColumns"
              :key="col.key"
              class="permission-cell"
            >
              <span v-if="hasPermission(role, col.key)" class="yes-text">YES</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { arrayProp, objectProp } from "@/helpers/props";
import { mapActions, mapState } from "vuex";
import useToastHandler from "@/services/toastHandler";

const PERMISSION_COLUMNS = [
  { key: "customer", label: "ALL CUSTOMERS" },
  { key: "bdm_customer", label: "BDM CUSTOMERS" },
  { key: "bdm_dashboard", label: "DASHBOARD BDM ONLY" },
  { key: "dashboard", label: "DASHBOARD ALL" },
  { key: "trading", label: "TRADING" },
  { key: "marketing", label: "MARKETING ALL" },
  { key: "bdm_marketing", label: "MARKETING BDM" },
  { key: "bdm", label: "BDM ALL" },
  { key: "only_bdm", label: "BDM ONLY" },
  { key: "finance", label: "FINANCE" },
  { key: "promotions", label: "PROMOTIONS" },
  { key: "settings", label: "SETTINGS" },
];

export default {
  props: {
    permissionConfig: objectProp(),
    resources: arrayProp(),
  },
  data() {
    return {
      roleList: [],
      permissionColumns: PERMISSION_COLUMNS,
    };
  },
  watch: {
    loadingCreateRole(val) {
      if (!val) this.fetchListRolePermission();
    },
    loadingDeleteRole(val) {
      if (!val) this.fetchListRolePermission();
    },
  },
  computed: {
    ...mapState("user/admin", ["loadingDeleteRole", "loadingCreateRole"]),
  },
  async mounted() {
    await this.fetchListRolePermission();
  },
  methods: {
    ...mapActions({
      getRoleList: "user/admin/getRoleList",
    }),
    hasPermission(role, key) {
      if (role.hasFullAccess) return true;
      return role.permissionCodes.includes(key);
    },
    processPermissions(permissionsList) {
      const hasFullAccess = permissionsList.some(
        (p) => p.code === "full_access" || p.code?.includes("full")
      );
      const permissionCodes = permissionsList.map((p) => p.code || p.name).filter(Boolean);
      return { hasFullAccess, permissionCodes };
    },
    async fetchListRolePermission() {
      try {
        const data = await this.getRoleList({
          page: 1,
          limit: 100,
          additionalDetail: "withPermissions",
        });
        if (!data)
          return this.showToastError(false, "Error fetching role list");

        this.roleList = data.data.map((i) => {
          const { hasFullAccess, permissionCodes } = this.processPermissions(
            i.permissions
          );
          return {
            id: i.code,
            name: i.name,
            hasFullAccess,
            permissionCodes,
          };
        });
      } catch (error) {
        console.error("Error fetching role permissions:", error);
        this.showToastError(false, "Error fetching role list");
      }
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();
    return { showToastError, showToastSuccess };
  },
};
</script>

<style scoped>
.permissions-matrix {
  background: #f8f9fa;
}

.table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.role-column {
  min-width: 140px;
  background: #f8f9fa;
  vertical-align: middle;
  font-weight: 700;
  font-size: 12px;
  color: #6c757d;
  text-align: center;
}

.permission-header {
  min-width: 90px;
  text-align: center;
  background: #f8f9fa;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  padding: 12px 8px;
  vertical-align: middle;
}

.role-info {
  font-weight: 700;
  font-size: 13px;
  color: #212529;
  background: #f8f9fa;
  vertical-align: middle;
  padding: 12px 16px;
}

.permission-cell {
  text-align: center;
  padding: 12px 8px;
  background: white;
  vertical-align: middle;
}

.yes-text {
  font-weight: 700;
  font-size: 12px;
  color: #212529;
}
</style>
